import React, { useEffect, useState, useMemo } from "react";
import { Button, Card, Col, Row, Modal, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import PageLoader from "../components/PageLoader";
import { categoriesApi } from "../api/categories";

// ============================================================
// Helpers
// ============================================================
const slugify = (str) =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const EMPTY_FORM = {
  name: "",
  slug: "",
  description: "",
  parent_id: "",
  sort_order: 0,
};

export default function CategoriesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");

  // --- Modal édition / création ---
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null); // null = création
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // --- Modal suppression ---
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ============================================================
  // Chargement
  // ============================================================
  const loadData = () => {
    setLoading(true);
    categoriesApi
      .list()
      .then((data) => setItems(data || []))
      .catch((err) => console.error("Erreur chargement catégories:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  // ============================================================
  // Hiérarchie : map parent → nom
  // ============================================================
  const parentName = useMemo(() => {
    const map = {};
    items.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [items]);

  // ============================================================
  // Filtrage + pagination
  // ============================================================
  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.slug?.toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safePage, perPage]);

  useEffect(() => { setCurrentPage(1); }, [search, perPage]);

  // ============================================================
  // Ouverture modal création / édition
  // ============================================================
  const openCreate = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setFormOpen(true);
  };

  const openEdit = (cat) => {
    setEditingId(cat.id);
    setFormData({
      name: cat.name || "",
      slug: cat.slug || "",
      description: cat.description || "",
      parent_id: cat.parentId || "",
      sort_order: cat.sortOrder || 0,
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
  };

  // ============================================================
  // Changements formulaire
  // ============================================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => {
      const next = { ...f, [name]: value };
      // Auto-slug en création si l'utilisateur n'a pas encore touché au slug
      if (name === "name" && !editingId && (!f.slug || f.slug === slugify(f.name))) {
        next.slug = slugify(value);
      }
      return next;
    });
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // ============================================================
  // Soumission
  // ============================================================
  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = "Le nom est requis.";
    if (!formData.slug.trim()) errs.slug = "Le slug est requis.";
    if (formData.slug && !/^[a-z0-9-]+$/.test(formData.slug)) {
      errs.slug = "Lettres minuscules, chiffres et tirets uniquement.";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim() || null,
        parent_id: formData.parent_id || null,
        sort_order: Number(formData.sort_order) || 0,
      };
      if (editingId) {
        await categoriesApi.update(editingId, payload);
      } else {
        await categoriesApi.create(payload);
      }
      setFormOpen(false);
      loadData();
    } catch (err) {
      alert("Erreur : " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Suppression
  // ============================================================
  const askDelete = (cat) => setDeleteTarget({ id: cat.id, name: cat.name });

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await categoriesApi.destroy(deleteTarget.id);
      setItems((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      alert("Erreur lors de la suppression : " + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  // ============================================================
  // Loader
  // ============================================================
  if (loading) {
    return (
      <React.Fragment>
        <Header onSkin={setSkin} />
        <div className="main main-app cere-main p-3 p-lg-4">
          <PageLoader message="Chargement des catégories…" />
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  // ============================================================
  // Rendu
  // ============================================================
  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app cere-main p-3 p-lg-4">

        {/* En-tête */}
        <div className="cere-page-head d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb cere-breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><Link to="/dashboard">Admin</Link></li>
                <li className="breadcrumb-item active">Catégories</li>
              </ol>
            </nav>
            <h4 className="cere-page-title mb-0">Catégories</h4>
            <p className="cere-page-subtitle mb-0">
              Organisez les documents par famille : cadre juridique, résultats, rapports…
            </p>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button className="cere-btn cere-btn-primary" onClick={openCreate}>
              <i className="ri-add-line me-2"></i>
              Nouvelle catégorie
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <Card className="cere-card cere-toolbar-card mb-4">
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col md="7">
                <div className="cere-search-input">
                  <i className="ri-search-line"></i>
                  <input
                    type="text"
                    placeholder="Rechercher une catégorie…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="cere-search-clear"
                      onClick={() => setSearch("")}
                      aria-label="Effacer la recherche"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </Col>
              <Col md="5" className="d-flex align-items-center gap-2 justify-content-md-end">
                <span className="cere-toolbar-label">Afficher</span>
                <select
                  className="cere-select"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="cere-toolbar-label">par page</span>
                <span className="cere-toolbar-label ms-3">
                  {filtered.length} catégorie{filtered.length > 1 ? "s" : ""}
                </span>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau */}
        {filtered.length === 0 ? (
          <Card className="cere-card cere-empty-state">
            <Card.Body>
              <div className="cere-empty-icon">
                <i className="ri-price-tag-3-line"></i>
              </div>
              <h5 className="cere-empty-title">Aucune catégorie</h5>
              <p className="cere-empty-desc">
                {search
                  ? `Aucun résultat pour « ${search} ».`
                  : "Commencez par créer votre première catégorie."}
              </p>
              <Button className="cere-btn cere-btn-primary" onClick={openCreate}>
                <i className="ri-add-line me-2"></i>
                Créer une catégorie
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <Card className="cere-card cere-table-card">
            <div className="table-responsive">
              <table className="cere-table-docs">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>#</th>
                    <th>Nom</th>
                    <th>Slug</th>
                    <th>Parent</th>
                    <th style={{ width: 90 }}>Ordre</th>
                    <th style={{ width: 120 }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((cat) => (
                    <tr key={cat.id}>
                      <td><span className="cere-doc-id">#{cat.id}</span></td>
                      <td>
                        <div className="cere-doc-info">
                          <div className="cere-doc-info-icon">
                            <i className="ri-price-tag-3-line"></i>
                          </div>
                          <div className="cere-doc-info-text">
                            <span
                              className="cere-doc-info-title has-tooltip"
                              data-tooltip={cat.name}
                            >
                              {cat.name}
                            </span>
                            {cat.description && (
                              <div className="cere-doc-info-slug has-tooltip" data-tooltip={cat.description}>
                                {cat.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td><code>{cat.slug}</code></td>
                      <td>
                        {cat.parentId && parentName[cat.parentId] ? (
                          <span className="cere-chip cere-chip--blue">
                            {parentName[cat.parentId]}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{cat.sortOrder ?? 0}</td>
                      <td className="text-end">
                        <div className="cere-doc-actions">
                          <button
                            className="cere-icon-btn"
                            onClick={() => openEdit(cat)}
                            title="Éditer"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            className="cere-icon-btn cere-icon-btn--danger"
                            onClick={() => askDelete(cat)}
                            title="Supprimer"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="cere-pagination-wrapper">
            <div className="cere-pagination">
              <button
                className="cere-page-btn"
                disabled={safePage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                aria-label="Page précédente"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {pageNumbers({ totalPages, safePage }).map((p, i) =>
                p === "…" ? (
                  <span key={`e-${i}`} className="cere-page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`cere-page-btn ${p === safePage ? "active" : ""}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="cere-page-btn"
                disabled={safePage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Page suivante"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
            <div className="cere-pagination-info">
              Page {safePage} sur {totalPages}
            </div>
          </div>
        )}

        {/* ============================================================
            MODAL CRÉATION / ÉDITION
            ============================================================ */}
        <Modal
          show={formOpen}
          onHide={closeForm}
          centered
          size="lg"
          backdrop="static"
          className="cere-modal-form"
        >
          <Modal.Header closeButton={!saving}>
            <Modal.Title>
              <i className={editingId ? "ri-edit-line" : "ri-add-line"}></i>
              {editingId ? "Modifier la catégorie" : "Nouvelle catégorie"}
            </Modal.Title>
          </Modal.Header>

          <form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row className="g-3">
                <Col md="8">
                  <label className="cere-form-label">
                    Nom <span className="cere-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`cere-form-control ${formErrors.name ? "is-invalid" : ""}`}
                    placeholder="Ex : Cadre juridique"
                    autoFocus
                  />
                  {formErrors.name && <div className="cere-form-error">{formErrors.name}</div>}
                </Col>

                <Col md="4">
                  <label className="cere-form-label">Ordre</label>
                  <input
                    type="number"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleChange}
                    className="cere-form-control"
                    min="0"
                  />
                </Col>

                <Col md="12">
                  <label className="cere-form-label">
                    Slug <span className="cere-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className={`cere-form-control ${formErrors.slug ? "is-invalid" : ""}`}
                    placeholder="cadre-juridique"
                  />
                  {formErrors.slug ? (
                    <div className="cere-form-error">{formErrors.slug}</div>
                  ) : (
                    <div className="cere-form-help">
                      Identifiant unique (lettres minuscules, chiffres, tirets)
                    </div>
                  )}
                </Col>

                <Col md="12">
                  <label className="cere-form-label">Catégorie parente</label>
                  <select
                    name="parent_id"
                    value={formData.parent_id}
                    onChange={handleChange}
                    className="cere-form-control"
                  >
                    <option value="">— Aucune (catégorie racine) —</option>
                    {items
                      .filter((c) => c.id !== editingId)
                      .map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                  </select>
                </Col>

                <Col md="12">
                  <label className="cere-form-label">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="cere-form-control"
                    placeholder="Description courte de la catégorie…"
                  />
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <button
                type="button"
                className="cere-btn cere-btn-outline"
                onClick={closeForm}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="cere-btn cere-btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-2"></i>
                    {editingId ? "Mettre à jour" : "Créer"}
                  </>
                )}
              </button>
            </Modal.Footer>
          </form>
        </Modal>

        {/* ============================================================
            MODAL SUPPRESSION
            ============================================================ */}
      <Modal
  show={!!deleteTarget}
  onHide={() => !deleting && setDeleteTarget(null)}
  centered
  backdrop="static"
  className="cere-modal-delete"
>
  <div className="cere-modal-delete-icon">
    <i className="ri-delete-bin-line"></i>
  </div>
  
  <Modal.Body className="text-center">
    <h4 className="cere-modal-delete-title">Confirmer la suppression</h4>
    <p className="cere-modal-delete-text">
      Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est définitive.
    </p>
    
    {deleteTarget && (
      <div className="cere-modal-delete-preview">
        <i className="ri-price-tag-3-line"></i>
        <div className="cere-modal-delete-preview-content">
          <span className="cere-modal-delete-preview-label">Catégorie ciblée</span>
          <span className="cere-modal-delete-preview-name">{deleteTarget.name}</span>
        </div>
      </div>
    )}
    
    <div className="cere-modal-delete-warning-box">
      <i className="ri-error-warning-line"></i>
      <span>Les documents liés à cette catégorie perdront leur classement.</span>
    </div>
  </Modal.Body>

  <Modal.Footer className="cere-modal-delete-footer">
    <button
      className="cere-btn cere-btn-outline"
      onClick={() => setDeleteTarget(null)}
      disabled={deleting}
    >
      Annuler
    </button>
    <button
      className="cere-btn cere-btn-danger"
      onClick={confirmDelete}
      disabled={deleting}
    >
      {deleting ? (
        <>
          <Spinner animation="border" size="sm" className="me-2" />
          Suppression…
        </>
      ) : (
        <>
          <i className="ri-delete-bin-line me-2"></i>
          Supprimer
        </>
      )}
    </button>
  </Modal.Footer>
</Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}

// ============================================================
// Helper : numéros de page avec ellipses
// ============================================================
function pageNumbers({ totalPages, safePage }) {
  const pages = [];
  const maxVisible = 5;
  if (totalPages <= maxVisible + 2) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push("…");
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push("…");
    pages.push(totalPages);
  }
  return pages;
}